import React, { useState, useEffect } from 'react';
import { Typography, Card, Progress, Row, Col, Statistic } from 'antd';
import { MobileOutlined, SettingOutlined } from '@ant-design/icons';
import axios from '../../config/axiosInstance';
import { useTheme } from "../../context/ThemeContext";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import UpcomingEventsCalendar from "./UpcomingEventsCalendar";

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const { Title } = Typography;

const Home = () => {
  const { darkTheme } = useTheme();
  const textColor = darkTheme ? "#FFFFFF" : "#000000";
  const [devices, setDevices] = useState([]);
  const [configuredPercentage, setConfiguredPercentage] = useState(0);
  const [configuredCount, setConfiguredCount] = useState(0);

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const response = await axios.get('/devices');
        const data = response.data;
        setDevices(data);

        // Calculates the number and percentage of configured devices
        const configuredDevices = data.filter(device => device.DeviceConfigId);
        const percentage = Math.round((configuredDevices.length / data.length) * 100);
        setConfiguredPercentage(percentage);
        setConfiguredCount(configuredDevices.length);
      } catch (error) {
        console.error('Error fetching devices:', error);
      }
    };
    fetchDevices();
  }, []);

  // Doughnut - Calculate number of mobile and desktop devices
  const mobileDevices = devices.filter((device) => device.Idiom === "Phone").length;
  const desktopDevices = devices.filter((device) => device.Idiom === "Desktop" || device.Idiom === "Tablet").length;

  const doughnutChartData = {
    labels: ["Mobile Devices", "Desktop Devices"],
    datasets: [
      {
        data: [mobileDevices || 0, desktopDevices || 0],
        backgroundColor: ["#36A2EB", "#FF6384"],
        borderWidth: 0,
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: { color: textColor, font: { size: 14 } },
      },
      datalabels: {
        color: "#FFF",
        font: { weight: "bold", size: 16 },
        formatter: (value, context) => {
          const total = context.dataset.data.reduce((acc, num) => acc + num, 0);
          if (total === 0) return "";
          return `${value}`;
        },
      },
    },
  };

  return (
    <div style={{ padding: 24, minHeight: 280 }}>
      <Title level={2} style={{ marginBottom: '40px' }}>
        Welcome to Dsync Dashboard
      </Title>

      <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
        <Col span={24}>
          <UpcomingEventsCalendar />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
        {/* First column: Devices Configuration */}
        <Col xs={24} sm={12} md={8} lg={6} style={{ display: "flex", minWidth: 0 }}>
          <Card
              hoverable={true}
              title={
                <div style={{ color: '#1B8AF1' }}>
                  <SettingOutlined style={{ marginRight: '10px', color: '#1B8AF1' }} />
                  Devices Configuration
                </div>
              }
              style={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: "100%",
                overflow: "hidden",
              }}
          >
            <div style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <Progress
                  type="circle"
                  percent={configuredPercentage}
                  format={percent => `${percent}%`}
                  strokeColor="#1B8AF1"
              />
              <Statistic
                  value={`${configuredCount}/${devices.length}`}
                  valueStyle={{ marginTop: '10px' }}
                  suffix="Configured"
              />
            </div>
          </Card>
        </Col>

        {/* Second column: Devices Distribution*/}
        <Col xs={24} sm={12} md={8} lg={6} style={{ display: "flex", minWidth: 0 }}>
          <Card
              hoverable={true}
              title={
                <div style={{ color: "#1B8AF1" }}>
                  <MobileOutlined style={{ marginRight: '10px', color: '#1B8AF1' }} />
                  Devices Distribution
                </div>
              }
              style={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: "100%",
                overflow: "hidden",
              }}
          >
            <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Doughnut data={doughnutChartData} options={doughnutOptions} />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Home;
